import { queryOptions, useQuery } from '@tanstack/react-query';

import { createApiClient } from 'lib/api/createApiClient';
import type { MarginTopHolder, MarginTopHolderResponse, MarginTopHoldersResponse } from 'types/perpsSocial';
import config from 'utils/config';

import {
    normalizePerpsPositionSide,
    normalizePerpsSocialTicker,
    PERPS_SOCIAL_STALE_TIME_MS,
    perpsSocialRetryDelay,
    shouldRetryPerpsSocialQuery,
    toFinancialString,
} from './perpsSocialQueryUtils';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

export const perpsMarginTopHoldersKeys = {
    all: ['perpsSocial', 'marginTopHolders'] as const,
    byTicker: (ticker: string) => [...perpsMarginTopHoldersKeys.all, normalizePerpsSocialTicker(ticker)] as const,
};

export interface PerpsMarginTopHoldersQueryOptions {
    enabled?: boolean;
}

function toStringOrEmpty(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function toNullableString(value: unknown): string | null {
    const normalized = toStringOrEmpty(value);
    return normalized.length > 0 ? normalized : null;
}

function toRank(value: unknown): number | null {
    const normalized = toFinancialString(value as string | number | null | undefined);
    if (normalized === null) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

export function normalizeMarginTopHolder(holder: MarginTopHolderResponse): MarginTopHolder {
    const nickname = toStringOrEmpty(holder.nickname) || toStringOrEmpty(holder.display_name);
    const socialId = toNullableString(holder.social_id);
    // Mobile profile navigation is nickname-based; social_id is supplemental
    // metadata and may legitimately be absent for a named public holder.
    const isAnonymous = holder.is_anonymous === true || nickname.length === 0;

    return {
        nickname,
        socialId,
        profileImagePath: toNullableString(holder.profile_image_path),
        value: toFinancialString(holder.value ?? holder.score),
        rank: toRank(holder.rank),
        isAnonymous,
        side: normalizePerpsPositionSide(holder.side),
    };
}

export async function fetchPerpsMarginTopHolders(ticker: string, signal?: AbortSignal): Promise<MarginTopHolder[]> {
    const normalizedTicker = normalizePerpsSocialTicker(ticker);
    if (!normalizedTicker) return [];

    const response = await api.get<MarginTopHoldersResponse>('/social/margin/top_holders', {
        params: { market_ticker: normalizedTicker },
        signal,
    });
    return (response.data.rank_list ?? []).map(normalizeMarginTopHolder);
}

export function perpsMarginTopHoldersQueryOptions(ticker: string, options?: PerpsMarginTopHoldersQueryOptions) {
    const normalizedTicker = normalizePerpsSocialTicker(ticker);

    return queryOptions({
        queryKey: perpsMarginTopHoldersKeys.byTicker(normalizedTicker),
        queryFn: ({ signal }) => fetchPerpsMarginTopHolders(normalizedTicker, signal),
        enabled: normalizedTicker.length > 0 && (options?.enabled ?? true),
        staleTime: PERPS_SOCIAL_STALE_TIME_MS,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        retry: shouldRetryPerpsSocialQuery,
        retryDelay: perpsSocialRetryDelay,
    });
}

export function usePerpsMarginTopHolders(ticker: string, options?: PerpsMarginTopHoldersQueryOptions) {
    return useQuery(perpsMarginTopHoldersQueryOptions(ticker, options));
}
